import SignalCellularAlt from '@mui/icons-material/SignalCellularAlt'
import { Box, Fade, Paper, Tooltip, Typography } from '@mui/material'
import { grey } from '@mui/material/colors'
import {
  orThrow,
  selectLayout,
  type Participant,
  type ZoomClient,
  type ZoomMediaStream,
} from '@fireflyhealth/zoom-web'
import isEqual from 'lodash/isEqual'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSnackbar } from 'notistack'
import { logger } from '~/shared/lib/logger'
import { useMount, useUnmount } from '~/shared/hooks/useMount'
import { SessionParticipantsVideoPlayer } from './SessionParticipantsVideoPlayer'
import { useZoomSession } from './SessionProvider'
import { SessionToolbar } from './SessionToolbar'
import { SessionVideoPlayer } from './SessionVideoPlayer'
import { connectionMarker } from './utils'
import { fireAndForget } from '~/shared/lib/fireAndForget'

/** How long the provider can be idle before the toolbar fades, matching the member apps. */
const IDLE_TIMEOUT_MS = 1000 * 10

/**
 * Key for the provider's own connection remedy notice, so a degrade enqueues it once and a
 * recovery can dismiss that exact notice rather than clearing every snackbar on screen.
 */
const CONNECTION_NOTICE_KEY = 'zoom-self-connection'

/**
 * The video SDK rejects with a plain object carrying a `reason` string rather than an
 * `Error`, and reports an already-initializing camera through this one message.
 */
const isCameraAlreadyStartingError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'reason' in error &&
  error.reason === 'Camera is starting,please wait.'

/**
 * Props interface for the SessionVideoContainer component
 * Defines all the required properties for managing a Zoom video session
 */
interface SessionVideoContainerProps {
  /** Current user participant object containing user info and video state */
  currentUser: Participant
  /** Function to update the current user state when changes occur */
  setCurrentUser: (user: Participant) => void
  /** Zoom Stream API instance for managing video/audio streams */
  stream: ZoomMediaStream
  /** Boolean flag indicating if the current user's video is enabled */
  isVideoOn: boolean
  /** Function to toggle the current user's video on/off state */
  setIsVideoOn: (isVideoOn: boolean) => void
  /** Array of all participants currently in the session */
  allSessionParticipants: Participant[]
  /** Async function to handle leaving the video call session */
  handleLeaveCall: () => Promise<void>
  /** Zoom VideoClient instance for session management */
  client: ZoomClient
  /** Object mapping Zoom user ids to their network quality scores (0-3) */
  derivedNetworkQuality: Record<number, number>
  /** The current user's own effective network quality (0-3), or undefined until the SDK reports it */
  selfNetworkQuality: number | undefined
}

/**
 * SessionVideoContainer Component
 *
 * Main container component for managing Zoom video session UI and interactions for the current user.
 * This component handles the complete video call experience including:
 * - Video stream management (start/stop video)
 * - Audio stream management (start/stop/mute/unmute audio)
 * - Participant video display layout
 * - Session toolbar with controls
 * - Network quality monitoring
 * - User state synchronization
 *
 * The component automatically starts video on mount and cleans up on unmount.
 * It provides a responsive layout with the current user's video in the top-right
 * corner and other participants' videos in the main area.
 */
export const SessionVideoContainer: React.FC<SessionVideoContainerProps> = ({
  currentUser,
  setCurrentUser,
  stream,
  setIsVideoOn,
  isVideoOn,
  allSessionParticipants,
  handleLeaveCall,
  client,
  derivedNetworkQuality,
  selfNetworkQuality,
}) => {
  // Get session state and controls from the Zoom session context
  const {
    activeMicrophone,
    activeSpeaker,
    appointment,
    isAudioOn,
    isMuted,
    setIsMuted,
    setIsAudioOn,
  } = useZoomSession()
  const appointmentMemberId = appointment.patient?.toString()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  // The provider's own connection, worded and coloured the same way each tile's badge is (via the
  // shared `connectionMarker`), so the indicator and a tile can never disagree. `undefined` until
  // the SDK reports a link level, which reads as "show nothing".
  const selfMarker = connectionMarker(selfNetworkQuality)

  /**
   * Auto-hide the toolbar on idle, matching the member apps. Only the controls fade — the
   * provider's own video, the spotlight and the connection indicator stay put — and any pointer or
   * key activity brings them straight back.
   */
  const [toolbarHidden, setToolbarHidden] = useState(false)
  const idleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const startIdleTimer = () => {
    clearTimeout(idleTimer.current)
    idleTimer.current = setTimeout(() => setToolbarHidden(true), IDLE_TIMEOUT_MS)
  }
  const resetIdleTimer = useCallback(() => {
    setToolbarHidden(false)
    startIdleTimer()
  }, [])
  useEffect(() => {
    startIdleTimer()
    const activity = ['mousemove', 'mousedown', 'keydown', 'touchstart'] as const
    activity.forEach(event => window.addEventListener(event, resetIdleTimer))
    return () => {
      clearTimeout(idleTimer.current)
      activity.forEach(event => window.removeEventListener(event, resetIdleTimer))
    }
  }, [resetIdleTimer])

  /**
   * A one-shot remedy notice for the provider's own connection: shown once when it degrades to a
   * poor connection and cleared when it recovers, reusing the wording `app/`'s member call uses
   * where the link type is unknown. A recovery all the way to good also gets a brief confirmation.
   *
   * Two refs, because they answer two different questions: `noticeOpen` is whether the persistent
   * warning is on screen (so it is closed exactly once), and `recoveryOwed` is whether the provider
   * was warned and has not yet been told it recovered. Collapsing them into one flag drops the
   * confirmation on a gradual `poor -> ok -> good` recovery, which is the usual path since the
   * quality is `Math.min(uplink, downlink)` and the two directions recover at different times.
   */
  const noticeOpen = useRef(false)
  const recoveryOwed = useRef(false)
  useEffect(() => {
    const band = selfMarker?.band
    if (band === 'poor') {
      if (!noticeOpen.current) {
        noticeOpen.current = true
        recoveryOwed.current = true
        enqueueSnackbar('Weak connection. Try another network, or move closer to your router.', {
          key: CONNECTION_NOTICE_KEY,
          variant: 'warning',
          persist: true,
        })
      }
    } else {
      // No longer poor: close the persistent warning if it is up, even at the intermediate "ok"
      // band. The recovery confirmation waits for a full return to good, whichever band it passed.
      if (noticeOpen.current) {
        noticeOpen.current = false
        closeSnackbar(CONNECTION_NOTICE_KEY)
      }
      if (band === 'good' && recoveryOwed.current) {
        recoveryOwed.current = false
        enqueueSnackbar('Connection restored.', { variant: 'success' })
      }
    }
  }, [selfMarker?.band, enqueueSnackbar, closeSnackbar])

  // The remedy notice persists (no auto-hide), so close it if the container unmounts mid-degrade —
  // otherwise leaving the call would strand it on the pre-call screen, and a rejoin would stack a
  // second under the same key.
  useEffect(() => () => closeSnackbar(CONNECTION_NOTICE_KEY), [closeSnackbar])
  // The member takes the spotlight and everyone else (the provider's own self-view) the
  // thumbnail strip. `selectLayout` matches the spotlight by display name and keys the split
  // on `userId`, so the member joining from a second screen shows a second thumbnail rather
  // than colliding with the spotlight.
  const { spotlight: spotlightUser, others } = selectLayout(
    allSessionParticipants,
    appointmentMemberId,
  )

  /**
   * Handles media state changes for the current user
   * Synchronizes the local user state with the Zoom client state when media changes occur
   * This ensures the UI reflects the actual video/audio state from the Zoom SDK
   */
  const handleMediaStateChange = useCallback(() => {
    // Exit early if current user is not properly initialized
    if (currentUser.userId == null) return

    const refreshedCurrentUserInfo = client.getCurrentUserInfo()

    // Update current user state if there are meaningful changes
    if (
      refreshedCurrentUserInfo?.bVideoOn != null &&
      !isEqual(currentUser, refreshedCurrentUserInfo)
    ) {
      setCurrentUser(refreshedCurrentUserInfo)
    }
  }, [client, currentUser, setCurrentUser])
  /**
   * Initializes and starts the audio stream for the current user on the chosen input and output.
   *
   * On SDK 2.4.5 `startAudio` accepts `microphoneId`/`speakerId` directly ("if not specified, use
   * system default"), so the provider's pre-call choice is opened straight away rather than starting
   * on the system default and following up with a `switchMicrophone`. `app/`'s web adapter passes the
   * ids the same way, and a browser pass confirmed the device actually changes.
   */
  const handleStartAudio = useCallback(async () => {
    // Start audio automatically in Safari - required for proper audio initialization
    await orThrow(
      'startAudio',
      stream.startAudio({
        autoStartAudioInSafari: true,
        mute: isMuted,
        backgroundNoiseSuppression: true,
        highBitrate: true,
        microphoneId: activeMicrophone?.deviceId,
        speakerId: activeSpeaker?.deviceId,
      }),
    )
    logger.info('[Zoom] startAudio', {
      microphoneChosen: Boolean(activeMicrophone),
      speakerChosen: Boolean(activeSpeaker),
    })
    setIsAudioOn(true)
  }, [activeMicrophone, activeSpeaker, isMuted, setIsAudioOn, stream])

  /**
   * Initializes and starts the video stream with optimal settings
   * Configures high-quality video and handles errors gracefully
   */
  const handleStartVideo = useCallback(async () => {
    try {
      logger.info('Joining video stream')

      await stream.startVideo({
        hd: true, // Enable HD video quality
        fullHd: true, // Enable Full HD if supported
        ptz: stream.isBrowserSupportPTZ(), // Enable pan-tilt-zoom if supported
        originalRatio: true, // Maintain original aspect ratio
        mirrored: true, // Mirror video for natural self-view
      })
      setIsVideoOn(true)
      handleMediaStateChange()
      logger.info('Joining video stream complete')
    } catch (e) {
      // Handle specific error cases with appropriate user feedback
      if (isCameraAlreadyStartingError(e)) {
        // Camera is already initializing - show info message
        enqueueSnackbar('Camera is starting, please wait.', { variant: 'info' })
      } else {
        // General video start failure - show error message
        enqueueSnackbar('Failed to start video', { variant: 'error' })
      }
      logger.warn('handleStartVideo', e)
    }
  }, [stream, setIsVideoOn, handleMediaStateChange, enqueueSnackbar])

  const joinZoomAV = useCallback(async () => {
    try {
      logger.info('Joining av stream')

      if (isVideoOn) {
        await handleStartVideo()
      }

      await handleStartAudio()

      // Sync media state after joining stream
      handleMediaStateChange()
      logger.info('Self video attachment complete')
    } catch (e) {
      // handleStartVideo reports its own failures, so only the audio step reaches here.
      enqueueSnackbar('Failed to start audio', { variant: 'error' })
      logger.warn('joinZoomAV', e)
    }
  }, [isVideoOn, handleStartAudio, handleMediaStateChange, handleStartVideo, enqueueSnackbar])

  /**
   * Stops the current user's video stream and updates state
   * Performs cleanup and state synchronization after stopping video
   */
  const handleStopVideo = useCallback(async () => {
    logger.info('Stopping self video')
    setIsVideoOn(false)
    await stream.stopVideo()
    handleMediaStateChange()
    logger.info('Stopped self video')
  }, [stream, handleMediaStateChange, setIsVideoOn])

  // Lifecycle hooks: automatically start video on mount and clean up on unmount
  useMount(() => {
    // `void`, not fireAndForget: joinZoomAV reports its own failures to the provider and resolves.
    void joinZoomAV()
  })
  useUnmount(() => {
    logger.info('Unmounting Session container.')
    // When the current component unmounts - refresh the state of the current user
    // but do not leave the session yet. The parent component is responsible for handling
    // all clean up activity.
    handleMediaStateChange()
  })

  /**
   * Stops the audio stream completely
   * Used for cleanup or when audio needs to be fully disabled
   */
  const handleStopAudio = useCallback(async () => {
    await stream.stopAudio()
    setIsAudioOn(false)
  }, [setIsAudioOn, stream])

  /**
   * Mutes the current user's audio while keeping the stream active
   * Updates the local muted state to reflect the change
   */
  const handleMuteAudio = useCallback(async () => {
    await stream.muteAudio()
    setIsMuted(true)
  }, [stream, setIsMuted])

  /**
   * Unmutes the current user's audio
   * Updates the local muted state to reflect the change
   */
  const handleUnmuteAudio = useCallback(async () => {
    await stream.unmuteAudio()
    setIsMuted(false)
  }, [stream, setIsMuted])

  /**
   * Toggles the microphone mute state with robust error handling
   * Handles complex scenarios like starting audio when unmuting if not already active
   * Includes fallback logic to restart audio if unmuting fails
   */
  const handleToggleMicrophone = useCallback(async () => {
    if (isMuted) {
      // Unmuting: start audio if not already active, then unmute
      if (!isAudioOn) await handleStartAudio()
      await handleUnmuteAudio().catch(async () => {
        // Fallback: if unmuting fails, try restarting audio completely
        await handleStopAudio().catch()
        await handleStartAudio().catch()
      })
      handleMediaStateChange()
    } else {
      // Muting: simply mute the audio
      await handleMuteAudio()
    }
  }, [
    isMuted,
    isAudioOn,
    handleStartAudio,
    handleUnmuteAudio,
    handleMediaStateChange,
    handleStopAudio,
    handleMuteAudio,
  ])

  /**
   * Determine feedback message based on session state
   * Shows appropriate loading/waiting messages to users during different phases
   * Note: Both conditions check for empty users array but serve different semantic purposes
   */
  let feedback: string | null = null
  if (!allSessionParticipants.length) feedback = 'Connecting to session'
  if (spotlightUser === undefined) feedback = 'Waiting for other participants'

  return (
    <Box
      sx={{
        position: 'relative',
        flex: 1,
        bgcolor: grey[900], // Dark background for video container
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden', // Prevent content overflow
      }}
    >
      {/*
        The provider's own connection indicator - top-left, and deliberately outside the toolbar's
        idle fade: a connection going bad is exactly what a provider needs to see while the controls
        are away. Hidden until the SDK reports a link level.
      */}
      {selfMarker ? (
        <Tooltip title={`Your connection: ${selfMarker.text}`} placement="right">
          <Box
            role="img"
            aria-label={`Your connection: ${selfMarker.text}`}
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              zIndex: 200,
              display: 'flex',
              alignItems: 'center',
              borderRadius: '1em',
              p: 0.5,
              backgroundColor: 'rgba(0,0,0,0.35)',
            }}
          >
            <SignalCellularAlt sx={{ color: selfMarker.color }} />
          </Box>
        </Tooltip>
      ) : null}
      {/*
        Video overlay for all provider users - positioned in top-right corner
        This creates a "picture-in-picture" effect where the provider can see themselves
        while the main area shows other participants
      */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          p: 2,
          position: 'absolute',
          top: 0,
          width: '100%',
          zIndex: 150, // High z-index to appear above other content
        }}
      >
        <Fade in={true}>
          <Paper
            elevation={3}
            sx={{ overflow: 'hidden', position: 'relative', backgroundColor: 'transparent' }}
          >
            {/*
              Note: This uses SessionParticipantsVideoPlayer with users array for self-video
              The isSelfVideo=true flag ensures proper sizing and behavior for the provider users
            */}
            <SessionParticipantsVideoPlayer
              stream={stream}
              users={others}
              networkQuality={derivedNetworkQuality}
            />
          </Paper>
        </Fade>
      </Box>
      {/*
        Main content area - displays the primary participant's video (appointment member)
        This area takes up the full container and centers the main participant's video
        The appointment member is typically the patient/client in the video call
      */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          position: 'absolute',
          height: '100%',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'transparent',
        }}
      >
        {/*
          Render the appointment member's video if they are present in the session
          This is the primary participant (usually the patient) and gets the main video area
        */}
        {spotlightUser ? (
          <SessionVideoPlayer
            user={spotlightUser}
            stream={stream}
            networkQuality={derivedNetworkQuality[spotlightUser.userId]}
            isSpotlightVideo={true}
          />
        ) : null}
      </Box>
      {/* Feedback overlay - shows connection/waiting messages when needed */}
      {feedback ? (
        <Box sx={{ position: 'absolute', top: '45%', width: '100%' }}>
          <Box sx={{ my: 2, color: '#FFF' }}>
            <Typography align="center">{feedback}</Typography>
          </Box>
        </Box>
      ) : null}

      {/*
        Session controls toolbar - positioned at bottom of container. Wrapped so the whole chrome
        fades out on idle and comes back on pointer/key activity; the opacity cascades to the
        absolutely-positioned toolbar inside, and `pointerEvents: none` keeps a faded control from
        being clicked.
      */}
      <Box
        data-testid="zoom-toolbar-chrome"
        // Inline style rather than `sx` so the opacity is an inline attribute a test can read;
        // `sx` compiles to an injected CSS class that jsdom/happy-dom does not resolve.
        style={{
          transition: 'opacity 0.3s ease',
          opacity: toolbarHidden ? 0 : 1,
          pointerEvents: toolbarHidden ? 'none' : 'auto',
        }}
      >
        <SessionToolbar
          helpEvents={[]}
          newHelpEvent={false}
          // Camera device switching with error handling
          onChangeCamera={deviceId => {
            void stream
              .switchCamera(deviceId)
              .catch(e => logger.warn('Zoom: Switch camera failed', e))
          }}
          // Toggle camera on/off
          onToggleCamera={() => {
            if (isVideoOn) {
              fireAndForget('[Zoom] stop video', handleStopVideo)
            } else {
              // Unlike stopping, starting already snackbars and logs its own failure.
              void handleStartVideo()
            }
          }}
          // Microphone device switching (unmutes first to ensure audio is active)
          onChangeMicrophone={deviceId =>
            fireAndForget('[Zoom] switch microphone', async () => {
              await handleUnmuteAudio()
              await stream.switchMicrophone(deviceId)
            })
          }
          // Toggle microphone mute/unmute
          onToggleMicrophone={() =>
            fireAndForget('[Zoom] toggle microphone', handleToggleMicrophone)
          }
          // Speaker device switching
          onChangeSpeaker={deviceId => {
            fireAndForget('[Zoom] switch speaker', () => stream.switchSpeaker(deviceId))
          }}
          // Leave call handler
          onLeaveCall={() => void handleLeaveCall()}
        />
      </Box>
    </Box>
  )
}
